import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls, secondaryButtonCls, destructiveButtonCls } from "../../components/ui-components/buttonStyles.js";
import Skeleton from "../../components/ui-components/Skeleton";
import Badge from "../../components/ui-components/Badge";
import Modal from "../../components/ui-components/Modal";
import { toast } from "../../components/ui-components/Toast";
import Input from "../../components/form-components/Input";
import { useRoles, useCapabilityCatalog, updateRole, deleteRole } from "./api";
import { friendlyRoleError } from "./roleFormUtils";
import { CapabilityPickerList } from "./capabilityPicker";

// View / edit / delete an EXISTING organisation role, or view a read-only
// platform/template role. Reached at /settings/roles/:id. Both routes are
// gated by roles.assign at the router level (see src/index.js).
//
// Sub-project E2, Task 6: this component used to ALSO handle CREATE (at
// /settings/roles/new, optionally seeded from a template via
// location.state.cloneFrom) — that path now belongs to the governed
// create-from-template wizard (./OrgRoleWizard), which ends in a
// control-check before the role can go active. CustomRoleEditor keeps only
// the view/edit/delete responsibilities for a role that already exists.
export default function CustomRoleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // includeHidden: true so the scope-only platform/template roles
  // (cost_owner / tech_cost_owner) are still findable here even though the
  // catalog page hides them by default — they're valid direct links.
  const rolesQ = useRoles({ includeHidden: true });
  const capsQ = useCapabilityCatalog();
  const roles = rolesQ.data || [];
  const capabilityCatalog = capsQ.data || [];
  const isLoading = rolesQ.isLoading || capsQ.isLoading;

  const editingRole = roles.find((r) => String(r.id) === String(id));

  const [name, setName] = useState(editingRole?.name || "");
  const [purpose, setPurpose] = useState(editingRole?.purpose || "");
  const [selectedCaps, setSelectedCaps] = useState(editingRole?.capabilities || []);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Seed the form fields once the role loads (or a refetch changes it) — a
  // plain useState above already covers the common case (data present on
  // first render from cache), this effect only matters for the initial
  // fetch / a background refetch actually changing the underlying role.
  const seededRoleId = useRef(null);
  useEffect(() => {
    if (!editingRole) return;
    if (seededRoleId.current === editingRole.id) return;
    seededRoleId.current = editingRole.id;
    setName(editingRole.name || "");
    setPurpose(editingRole.purpose || "");
    setSelectedCaps(editingRole.capabilities || []);
  }, [editingRole]);

  const toggleCap = (capCode) => {
    setSelectedCaps((prev) => (prev.includes(capCode) ? prev.filter((c) => c !== capCode) : [...prev, capCode]));
  };

  const invalidateRoles = () => queryClient.invalidateQueries({ queryKey: ["roles"] });

  const updateMut = useMutation({
    mutationFn: (body) => updateRole(id, body),
    onSuccess: () => {
      invalidateRoles();
      toast.success("Role updated");
    },
    onError: (err) => toast.error(friendlyRoleError(err, "update")),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteRole(id),
    onSuccess: () => {
      invalidateRoles();
      setConfirmDelete(false);
      toast.success("Role deleted");
      navigate("/settings/roles");
    },
    onError: (err) => {
      setConfirmDelete(false);
      toast.error(friendlyRoleError(err, "delete"));
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    updateMut.mutate({ name: name.trim(), purpose: purpose.trim() || undefined, capabilities: selectedCaps });
  };

  // Platform and template roles are both immutable (backend rejects
  // PUT/DELETE with ROLE_IMMUTABLE) — only organisation roles are editable.
  const readOnly = editingRole?.type === "platform" || editingRole?.type === "template";
  const roleKindLabel = editingRole?.type === "platform" ? "Platform role" : editingRole?.type === "template" ? "Template role" : null;
  const pageTitle = editingRole?.name || "Role";

  useSetHeaderRecordName(editingRole?.name || null);

  // -- Loading --
  if (isLoading || (!editingRole && rolesQ.isFetching)) {
    return (
      <div className="p-8">
        <Skeleton height={320} />
      </div>
    );
  }

  // -- Not found --
  if (!editingRole) {
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-danger mb-2">Role not found</div>
            <button type="button" className={secondaryButtonCls} onClick={() => navigate("/settings/roles")}>
              Back to roles
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {pageTitle}
          </div>
          {readOnly && roleKindLabel && <Badge status="info" label={roleKindLabel} size="sm" />}
        </div>
      </HeaderPortal>

      <HeaderPortal>
        <div className="flex items-center gap-2.5">
          <button type="button" className={secondaryButtonCls} onClick={() => navigate("/settings/roles")}>
            {readOnly ? "Back" : "Cancel"}
          </button>
          {readOnly ? (
            // Platform roles aren't templates — no clone affordance at all.
            editingRole?.type === "template" && (
              <button
                type="button"
                className={primaryButtonCls}
                onClick={() => navigate("/settings/roles/new", { state: { cloneFrom: editingRole.code } })}
              >
                Use template
              </button>
            )
          ) : (
            <>
              <button
                type="button"
                className={destructiveButtonCls}
                onClick={() => setConfirmDelete(true)}
                disabled={deleteMut.isPending}
                title={editingRole?.assigned_user_count ? `Assigned to ${editingRole.assigned_user_count} user(s)` : undefined}
              >
                Delete
              </button>
              <button type="button" className={primaryButtonCls} onClick={handleSave} disabled={updateMut.isPending}>
                {updateMut.isPending ? "Saving..." : "Save"}
              </button>
            </>
          )}
        </div>
      </HeaderPortal>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-[680px] space-y-6">
          {/* Profile */}
          <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
            <div className="border-b border-border pb-3 mb-4">
              <h3 className="text-sm font-semibold text-text">Profile</h3>
              <p className="text-xs text-text-2 mt-0.5">
                {readOnly
                  ? "Platform and template roles can't be edited — use a template to create an organisation role."
                  : "Name and describe this role."}
              </p>
            </div>

            <div className="space-y-4">
              <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                <SectionHeader title="Name" helperText="Shown throughout the app wherever roles are listed." />
                <div className="mt-3">
                  <Input
                    name="name"
                    type="text"
                    value={name}
                    onChange={readOnly ? undefined : (e) => setName(e.target.value)}
                    readOnly={readOnly}
                    noMarginBottom
                  />
                </div>
              </section>

              <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                <SectionHeader title="Purpose" helperText="Plain-language explanation of what this role is for." />
                <div className="mt-3">
                  <Input
                    name="purpose"
                    type="text"
                    value={purpose}
                    onChange={readOnly ? undefined : (e) => setPurpose(e.target.value)}
                    readOnly={readOnly}
                    noMarginBottom
                  />
                </div>
              </section>
            </div>
          </div>

          {/* Capabilities */}
          <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
            <div className="border-b border-border pb-3 mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-text">Capabilities</h3>
                <p className="text-xs text-text-2 mt-0.5">What this role grants access to.</p>
              </div>
              <span className="text-xs text-text-3 shrink-0">
                {selectedCaps.length} selected
              </span>
            </div>

            <CapabilityPickerList
              capabilityCatalog={capabilityCatalog}
              selectedCaps={selectedCaps}
              onToggle={toggleCap}
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>

      <Modal
        show={confirmDelete}
        close={() => setConfirmDelete(false)}
        title="Delete this role?"
        description={
          editingRole?.assigned_user_count
            ? `"${editingRole?.name}" is assigned to ${editingRole.assigned_user_count} user(s). It can't be deleted while assigned.`
            : `"${editingRole?.name}" will be permanently removed.`
        }
        confirmButton="Delete"
        cancelButton="Cancel"
        alertIcon
        confirmAction={() => deleteMut.mutate()}
        icon={icon({ name: "trash", style: "solid" })}
      />
    </div>
  );
}

function SectionHeader({ title, helperText }) {
  return (
    <div className="min-w-0">
      <div className="text-sm font-medium text-text">{title}</div>
      {helperText && <div className="mt-0.5 text-xs text-text-2">{helperText}</div>}
    </div>
  );
}
